import React, { useEffect, useState, useMemo } from "react";
import { collection, query, where, onSnapshot, doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { Shield, Check, Search } from "lucide-react";
import { RUOLI_OPERATIVI, PERMESSI_DEFAULT_PER_RUOLO } from "../utils/permessiDefaultPerRuolo";
import { sezioniDelRuolo } from "../utils/permessiEffettivi";

// Sezioni controllabili — sempre visibili a owner/admin
// Tab sidebar abilitabili
const TAB_SIDEBAR = [
  { id: "tab-tutto",      label: "Vista completa",  desc: "Mostra tutto — consigliato per titolari e PM" },
  { id: "tab-progetti",   label: "Tab Progetti",    desc: "Clienti e commesse" },
  { id: "tab-personale",  label: "Tab Personale",   desc: "Presenze, programmazione, note spese, attività, rapportini" },
  { id: "tab-acquisti",   label: "Tab Acquisti",    desc: "Centro acquisti, magazzino, scadenzario" },
  { id: "tab-gestione",   label: "Tab Gestione",    desc: "Centro operativo, dipendenti, clienti, verbali, impostazioni" },
];

export const SEZIONI = [
  { id: "report-ore",          label: "Report Ore",                desc: "Riepilogo ore lavorate per operaio e per commessa" },
  { id: "approvazione-cartellini",  label: "Approvazione Cartellini",  desc: "Approva o respingi i cartellini presenze inviati dagli operai" },
  { id: "approvazione-note-spese",  label: "Approvazione Note Spese",  desc: "Approva o respingi le note spese inviate dai dipendenti" },
  { id: "approvazione-ferie-permessi", label: "Approvazione Ferie e Permessi", desc: "Approva o respingi le richieste di ferie e permessi inviate dai dipendenti" },
  { id: "registro-rapportini",    label: "Registro rapportini",     desc: "Accesso al registro globale dei rapportini emessi" },
  { id: "control-center",      label: "Centro Operativo",          desc: "Dashboard commesse, scadenze, ore totali" },
  { id: "impianti",           label: "Anagrafica Impianti",       desc: "Gli impianti dei clienti — cabine, quadri, impianti di terra, colonnine. Base per le verifiche periodiche obbligatorie" },
  { id: "verifiche-periodiche", label: "Verifiche Periodiche Obbligatorie", desc: "La scheda «Verifiche normative» dentro lo Scadenzario: scadenze di legge sugli impianti dei clienti (DPR 462/01) — sola consultazione" },
  { id: "gestione-verifiche",   label: "Gestione Verifiche Periodiche",     desc: "Chi può censire schede di verifica, modificare periodicità e registrare le verifiche eseguite" },
  { id: "gestione-impianti",  label: "Modifica Anagrafica Impianti", desc: "Chi può creare, modificare ed eliminare impianti — chi ha solo «Anagrafica Impianti» li vede in sola lettura" },
  { id: "clienti",             label: "Clienti",                   desc: "Anagrafica clienti e dashboard Pro" },
  { id: "centro-evoluto",      label: "Centro Controllo Evoluto",  desc: "Analisi avanzata commesse" },
  { id: "magazzino",           label: "Richieste al magazzino",    desc: "Richieste materiali e gestione magazzino" },
  { id: "programmazione",      label: "Programmazione settimanale",desc: "Piano settimanale dipendenti" },
  { id: "pianificazione-team", label: "Capacità Produttiva",       desc: "Disponibilità operai e capacità produttiva" },
  { id: "dipendenti",          label: "Dipendenti",                desc: "Anagrafica dipendenti e collaboratori" },
  { id: "centro-acquisti",     label: "Centro Acquisti (NF/ODA)",  desc: "Nuove forniture e ordini acquisto" },
  { id: "scadenziario",        label: "Scadenze & Ricorrenze",     desc: "Scadenzario aziendale" },
  { id: "rientro-materiali",   label: "Rientro Materiali",         desc: "Registrazione rientro materiali dal cantiere" },
  { id: "le-mie-commesse",     label: "Le mie commesse (Mia Area)", desc: "Pannello personale con le commesse di cui è responsabile: importi, fatturato, saldo e una nota libera" },
  { id: "impostazioni-org",    label: "Impostazioni Aziendali",     desc: "Dati aziendali, logo, personalizzazione documenti — di default visibile solo al titolare" },
  { id: "gestione-parco-mezzi", label: "Gestione Parco Mezzi",      desc: "Aggiunge/rimuove auto, furgoni e attrezzature dall'anagrafica tracciata — di default visibile solo al titolare" },
  { id: "modifica-acquisti",   label: "Modifica sezione Acquisti (NF/ODA)", desc: "Chi può modificare la colonna Acquisti dentro Nuove Forniture e Ordini — un controllo più fine dell'accesso alla pagina, che resta aperto a chi vede Centro Acquisti" },
  { id: "approva-oda", label: "Approvazione Ordini di Acquisto", desc: "Chi può far passare un ODA da Bozza ad Approvato e annullarlo — separato da chi lo compila, per una vera separazione dei compiti. Di default solo titolare/owner." },
  { id: "chiudi-oda", label: "Chiusura Ordini di Acquisto", desc: "Chi può chiudere definitivamente un ODA dopo ricezione e fattura. Di default solo titolare/owner." },
  { id: "modifica-programmazione", label: "Modifica Programmazione Settimanale", desc: "Chi può inserire e cambiare i nominativi nel piano settimanale — un controllo più fine dell'accesso alla pagina, che resta aperto a chi vede Programmazione. Di default solo titolare e amministrazione." },
  { id: "modifica-magazzino", label: "Modifica Magazzino (evasione richieste)", desc: "Chi può segnare quanto è stato evaso su una richiesta materiali e chiuderla — un controllo più fine dell'accesso alla pagina, che resta aperto a chi vede Richieste al magazzino. Di default anche il ruolo Magazziniere." },
  { id: "scadenze-personale", label: "Scadenze contrattuali del personale", desc: "Chi vede gli avvisi su periodi di prova in scadenza, contratti a termine in scadenza e anzianità di servizio maturata. Dati sensibili sul personale: di default solo titolare e amministrazione." },
  { id: "suggerimenti-operativi", label: "Suggerimenti Operativi", desc: "Il pannello in Dashboard che segnala scadenze, ritardi e anomalie su tutte le commesse insieme — di default visibile a Titolare, Gestione aziendale e PM" },
  { id: "ditte-esterne", label: "Ditte esterne", desc: "Anagrafica delle imprese a cui si affidano lavori e confronto del lavoro affidato a ciascuna: crea e modifica le ditte, anche al volo dalla commessa. Gli importi: tutti per Gestione e Amministrazione, solo delle proprie commesse per il PM. Di default Gestione aziendale, Amministrazione e Project Manager — piano Pro" },
  { id: "ore-commesse", label: "Ore delle persone sulle commesse («Le mie ore»)", desc: "Vede, nella Cabina di Regia e nei report della commessa, le ore che i colleghi segnano in «Le mie ore»: sono un costo che pesa sul margine. Non vede la giornata minuto per minuto di nessuno. Di default Gestione aziendale, Amministrazione e Project Manager (titolare e admin sempre)" },
  { id: "cabina-regia", label: "Cabina di Regia (tutte le commesse)", desc: "Di default visibile a Titolare e Gestione aziendale su ogni commessa. Chi non ha questa abilitazione la vede comunque, ma solo sulle commesse di cui è responsabile — mai su quelle altrui" },
  { id: "contratti-manutenzione", label: "Contratti di Manutenzione (Mia Area)", desc: "Pannello personale in sola consultazione — scadenze contrattuali delle commesse di cui è responsabile. La creazione e la gestione dei contratti si fa da Contratti di Manutenzione (Gestione, vedi sotto) — di default visibile al ruolo Project Manager" },
  { id: "gestione-contratti-manutenzione", label: "Contratti di Manutenzione (Gestione)", desc: "Gestione centralizzata di tutti i contratti di manutenzione dell'organizzazione (creazione, modifica, eliminazione) — di default visibile a Titolare, Gestione aziendale, Amministrazione e Project Manager" },
  { id: "gestione-tab-visibilita", label: "Gestione visibilità tab", desc: "Può modificare quali tab vedono gli altri utenti" },
  { id: "cruscotto-scadenze", label: "Cruscotto Scadenze", desc: "Vista unica su tutto ciò che ha una scadenza (Scadenzario, Manutenzione, SAL) — di default visibile a Gestione aziendale, Amministrazione e Project Manager" },
  { id: "da-fatturare", label: "Richieste di fatturazione di tutti i PM", desc: "Vede e gestisce le richieste di fatturazione di tutti i PM: stato, numero e data della fattura. Senza questo permesso un PM vede solo le proprie. Di default Gestione aziendale e Amministrazione" },
  { id: "elaborazione-offerte", label: "Elaborazione e Emissione Offerte", desc: "Calcolo e invio preventivi ai clienti — di default visibile a Gestione aziendale e Project Manager" },
  { id: "registro-interventi", label: "Registro interventi", desc: "Accesso al registro globale dei rapporti di intervento — di default visibile a Gestione aziendale e Project Manager" },
  { id: "pratiche", label: "Pratiche e documenti dal cliente", desc: "Crea le pratiche delle commesse (connessione fotovoltaica, ENEA, DM 37/08, antimafia…), carica i documenti e li chiede al cliente — piano Pro" },
  { id: "sicurezza-cantiere", label: "Sicurezza di cantiere", desc: "Attiva la sicurezza sui cantieri, sceglie i responsabili e gestisce le checklist di tutte le commesse (il responsabile di un cantiere gestisce comunque il suo) — di default Gestione aziendale — piano Pro" },
  { id: "pratiche-riservati", label: "Documenti riservati delle pratiche", desc: "Vede e carica i documenti riservati, come le dichiarazioni dei familiari conviventi per l'antimafia" },
  { id: "documenti-subappaltatori", label: "Documenti dei subappaltatori", desc: "Consulta i documenti di idoneità di imprese subappaltatrici e lavoratori (esclusi quelli sanitari) — piano Gold" },
  { id: "documenti-subappaltatori-verifica", label: "Verifica documenti subappaltatori", desc: "Carica, approva e rifiuta i documenti, modifica il catalogo e invia le richieste; vede anche l'idoneità sanitaria" },
  { id: "moduli-commessa", label: "Attivare i moduli delle commesse", desc: "Dal pannello «⚙️ Personalizza» attiva e disattiva SAL, Cronoprogramma, Giornale, DDT, Manutenzione e gli altri moduli non economici su tutte le commesse (il responsabile di una commessa lo fa comunque sulla sua). I moduli economici — Controllo Economico, Report, Varianti — restano a titolare e amministratori. Di default Gestione aziendale e Project Manager" },
  { id: "sopralluoghi-senza-commessa", label: "Sopralluoghi senza commessa", desc: "Fa il sopralluogo prima che esista la commessa (cliente o potenziale cliente, legato alla Richiesta Commerciale) e lo collega alla commessa quando nasce — di default Project Manager; titolare e amministratori sempre. Abilitalo agli operativi che fanno sopralluoghi" },
  { id: "registro-sal", label: "Registro SAL", desc: "Stati Avanzamento Lavori — di default visibile a Gestione aziendale, Amministrazione e Project Manager" },
];

// ─────────────────────────────────────────────────────────────
// Raggruppamento tematico delle sezioni.
// L'elenco SEZIONI sopra è in ordine cronologico (come sono state aggiunte nel tempo):
// va benissimo per il codice, pessimo per chi deve configurare i permessi — 36 voci di
// fila, con i permessi "Modifica X" lontani dalla sezione a cui si riferiscono.
// Qui le riordiniamo per argomento, senza toccare SEZIONI: se un domani se ne aggiunge
// una e ci si dimentica di inserirla in un gruppo, finisce automaticamente in "Altro"
// invece di sparire dalla pagina.
// ─────────────────────────────────────────────────────────────
export const GRUPPI_SEZIONI = [
  // Prima di tutto: quali "viste" della sidebar vede questa persona. È la scelta più a
  // monte — decide interi blocchi di menu — quindi sta in cima, prima delle singole sezioni.
  { id:"viste",      label:"Viste della barra laterale", emoji:"🗂️",
    sezioni:["tab-tutto","tab-progetti","tab-personale","tab-acquisti","tab-gestione"] },
  { id:"commesse",   label:"Commesse e lavoro quotidiano", emoji:"📁",
    sezioni:["le-mie-commesse","cabina-regia","moduli-commessa","registro-interventi","registro-sal","pianificazione-team","programmazione","modifica-programmazione"] },
  { id:"persone",    label:"Persone e presenze", emoji:"👥",
    sezioni:["dipendenti","approvazione-cartellini","approvazione-note-spese","approvazione-ferie-permessi","report-ore","registro-rapportini","ore-commesse","ditte-esterne","scadenze-personale","documenti-subappaltatori","documenti-subappaltatori-verifica","pratiche","pratiche-riservati","sicurezza-cantiere"] },
  { id:"clienti",    label:"Clienti e commerciale", emoji:"🤝",
    sezioni:["clienti","elaborazione-offerte","sopralluoghi-senza-commessa","da-fatturare"] },
  { id:"materiali",  label:"Materiali e acquisti", emoji:"📦",
    sezioni:["magazzino","modifica-magazzino","centro-acquisti","modifica-acquisti","approva-oda","chiudi-oda","rientro-materiali","gestione-parco-mezzi"] },
  { id:"impianti",   label:"Impianti e verifiche normative", emoji:"⚡",
    sezioni:["impianti","gestione-impianti","verifiche-periodiche","gestione-verifiche"] },
  { id:"analisi",    label:"Analisi e controllo", emoji:"📊",
    sezioni:["control-center","centro-evoluto","suggerimenti-operativi","cruscotto-scadenze","scadenziario"] },
  { id:"config",     label:"Configurazione e contratti", emoji:"⚙️",
    sezioni:["impostazioni-org","gestione-tab-visibilita","contratti-manutenzione","gestione-contratti-manutenzione"] },
];

// Permessi di dettaglio che dipendono da una sezione principale: vengono mostrati
// rientrati subito sotto il loro "padre", così è evidente a colpo d'occhio che
// "Modifica Magazzino" non è una sezione a sé ma un affinamento di "Richieste al magazzino".
export const SEZIONE_PADRE = {
  "modifica-programmazione": "programmazione",
  "modifica-magazzino":      "magazzino",
  "modifica-acquisti":       "centro-acquisti",
  "approva-oda":             "centro-acquisti",
  "chiudi-oda":              "centro-acquisti",
  "gestione-impianti":       "impianti",
  "gestione-verifiche":      "verifiche-periodiche",
  "gestione-contratti-manutenzione": "contratti-manutenzione",
  "documenti-subappaltatori-verifica": "documenti-subappaltatori",
  "pratiche-riservati": "pratiche",
};

// Restituisce i gruppi con dentro le sezioni vere, nell'ordine giusto, più un gruppo
// "Altro" che raccoglie ciò che non è stato ancora assegnato: nessuna voce può sparire.
export function sezioniRaggruppate() {
  const assegnate = new Set();
  const gruppi = GRUPPI_SEZIONI.map(g => {
    const voci = [];
    g.sezioni.forEach(id => {
      const s = SEZIONI.find(x => x.id === id);
      if (s) { voci.push(s); assegnate.add(id); }
    });
    return { ...g, voci };
  }).filter(g => g.voci.length > 0);
  const orfane = SEZIONI.filter(s => !assegnate.has(s.id));
  if (orfane.length) gruppi.push({ id:"altro", label:"Altro", emoji:"📄", voci:orfane });
  return gruppi;
}

// I ruoli operativi vivono in un unico posto (permessiDefaultPerRuolo.js), riusato anche
// qui — così questa pagina e i permessi di default non possono mai disallinearsi.
const RUOLI = RUOLI_OPERATIVI;

const SEZIONI_GESTIONE = [
  "dipendenti", "clienti", "approvazione-cartellini", "approvazione-note-spese", "approvazione-ferie-permessi",
  "registro-rapportini", "registro-interventi", "registro-sopralluoghi", "abilitazioni", "impostazioni-org"
];

// Tutti i permessi che esistono in questa versione di Nexiora: salvati con le Abilitazioni
// («sezioniNote»), così quelli che nasceranno dopo partono dal loro default di ruolo (W417).
export function catalogoSezioni() {
  const tutte = new Set([...SEZIONI.map(x => x.id), ...SEZIONI_GESTIONE, ...Object.keys(SEZIONE_PADRE), ...Object.values(SEZIONE_PADRE)]);
  Object.values(PERMESSI_DEFAULT_PER_RUOLO).forEach(r => (r.sezioni || []).forEach(x => tutte.add(x)));
  return [...tutte].sort();
}

export default function AbilitazioniPage() {
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const [abilitazioni, setAbilitazioni] = useState({}); // { ruolo: Set<sezioneId> }
  const [maiSalvata, setMaiSalvata] = useState(false); // true finché mostra solo i default, mai personalizzata per questa azienda
  const [utenti, setUtenti] = useState([]);
  const [utentiCustom, setUtentiCustom] = useState({}); // { uid: Set<sezioneId> } — sezioni AGGIUNTE individualmente
  const [utentiRimossi, setUtentiRimossi] = useState({}); // { uid: Set<sezioneId> } — sezioni RIMOSSE individualmente
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("ruoli");
  const [cercaSez, setCercaSez] = useState("");
  // Primo gruppo aperto, gli altri chiusi: si vede subito che la pagina è navigabile
  // senza doverla scorrere tutta, ma non si parte con un muro di 36 righe.
  const [gruppiAperti, setGruppiAperti] = useState(() => ({ viste: true }));
  const [riepilogoAperto, setRiepilogoAperto] = useState({});
  const gruppi = useMemo(() => sezioniRaggruppate(), []);
  const tuttiAperti = gruppi.length > 0 && gruppi.every(g => gruppiAperti[g.id]);
  const [showPopupGestione, setShowPopupGestione] = useState(false);
  const [selezionatiGestione, setSelezionatiGestione] = useState(new Set());

  // Carica abilitazioni salvate
  useEffect(() => {
    if (!effectiveOrgId) return;
    const ref = doc(db, "abilitazioniSezioni", effectiveOrgId);
    return onSnapshot(ref, snap => {
      if (snap.exists()) {
        const data = snap.data();
        // Per ogni ruolo, se la sua chiave non è mai stata salvata (documento vecchio con i
        // nomi di ruolo precedenti, o semplicemente un ruolo mai toccato dal titolare) uso
        // il default reale di QUEL ruolo — non un array vuoto. Un ruolo esplicitamente
        // svuotato dal titolare (chiave presente, array vuoto) resta invece vuoto per
        // davvero: è una sua scelta, non un dato mancante.
        const ab = {};
        let almenoUnDefault = false;
        RUOLI.forEach(r => {
          const chiavePresente = data.ruoli && Object.prototype.hasOwnProperty.call(data.ruoli, r.id);
          if (chiavePresente) {
            // le scelte salvate, più i default dei permessi nati dopo il salvataggio (W417)
            ab[r.id] = sezioniDelRuolo(data, r.id);
          } else {
            ab[r.id] = new Set(PERMESSI_DEFAULT_PER_RUOLO[r.id]?.sezioni || []);
            almenoUnDefault = true;
          }
        });
        setAbilitazioni(ab);
        setMaiSalvata(almenoUnDefault);
        // Abilitazioni custom per utente
        const uc = {};
        Object.entries(data.utenti || {}).forEach(([uid, sezioni]) => {
          uc[uid] = new Set(sezioni);
        });
        setUtentiCustom(uc);
        const ur = {};
        Object.entries(data.utentiRimossi || {}).forEach(([uid, sezioni]) => {
          ur[uid] = new Set(sezioni);
        });
        setUtentiRimossi(ur);
      } else {
        // Nessuna configurazione salvata ancora — mostro esattamente i default che il
        // sistema sta già applicando davvero (stessa fonte della tabella di riferimento in
        // Impostazioni Aziendali), non più "tutti vedono tutto": prima la tabella e questa
        // pagina potevano raccontare due cose diverse sulla stessa organizzazione.
        const ab = {};
        RUOLI.forEach(r => { ab[r.id] = new Set(PERMESSI_DEFAULT_PER_RUOLO[r.id]?.sezioni || []); });
        setAbilitazioni(ab);
        setMaiSalvata(true);
      }
      setLoading(false);
    });
  }, [effectiveOrgId]);

  // Carica utenti
  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db, "users"), where("organizationId", "==", effectiveOrgId), where("enabled", "==", true)),
      snap => setUtenti(snap.docs.map(d => d.data()).filter(u => u.role !== "owner" && !u.isPlatformOwner)));
  }, [effectiveOrgId]);

  const toggleRuolo = (ruoloId, sezioneId) => {
    setAbilitazioni(prev => {
      const nuove = { ...prev };
      const set = new Set(nuove[ruoloId]);
      if (set.has(sezioneId)) set.delete(sezioneId);
      else set.add(sezioneId);
      nuove[ruoloId] = set;
      return nuove;
    });
  };

  // toggleUtenteSezione — cicla tra 3 stati per ogni sezione:
  // neutro (eredita dal ruolo) → aggiunta (forzata ON) → rimossa (forzata OFF) → neutro
  const toggleUtenteSezione = (uid, sezioneId, ruoloUtente) => {
    const aggiunto = utentiCustom[uid]?.has(sezioneId) || false;
    const rimosso = utentiRimossi[uid]?.has(sezioneId) || false;
    const ereditato = abilitazioni[ruoloUtente]?.has(sezioneId) || false;

    if (!aggiunto && !rimosso) {
      // Stato neutro → aggiunta forzata (se non già ereditato) o rimossa (se ereditato)
      if (ereditato) {
        // Ha già accesso dal ruolo → rimuoviglielo
        setUtentiRimossi(prev => {
          const n = { ...prev };
          n[uid] = new Set(n[uid] || []);
          n[uid].add(sezioneId);
          return n;
        });
      } else {
        // Non ha accesso dal ruolo → aggiungiglielo
        setUtentiCustom(prev => {
          const n = { ...prev };
          n[uid] = new Set(n[uid] || []);
          n[uid].add(sezioneId);
          return n;
        });
      }
    } else if (aggiunto) {
      // Era aggiunta → torna neutro
      setUtentiCustom(prev => {
        const n = { ...prev };
        const s = new Set(n[uid] || []);
        s.delete(sezioneId);
        n[uid] = s;
        return n;
      });
    } else {
      // Era rimossa → torna neutro
      setUtentiRimossi(prev => {
        const n = { ...prev };
        const s = new Set(n[uid] || []);
        s.delete(sezioneId);
        n[uid] = s;
        return n;
      });
    }
  };

  // Calcola sezioni effettive per un utente (ereditate + aggiunte - rimosse)
  const sezioniEffettive = (uid, ruoloUtente) => {
    const ereditate = abilitazioni[ruoloUtente] || new Set();
    const aggiunte = utentiCustom[uid] || new Set();
    const rimosse = utentiRimossi[uid] || new Set();
    const risultato = new Set([...ereditate, ...aggiunte]);
    rimosse.forEach(id => risultato.delete(id));
    return risultato;
  };

  const salva = async () => {
    setSaving(true);
    try {
      const ruoliObj = {};
      RUOLI.forEach(r => { ruoliObj[r.id] = [...(abilitazioni[r.id] || [])]; });
      const utentiObj = {};
      Object.entries(utentiCustom).forEach(([uid, set]) => { utentiObj[uid] = [...set]; });
      const utentiRimossiObj = {};
      Object.entries(utentiRimossi).forEach(([uid, set]) => { utentiRimossiObj[uid] = [...set]; });
      // sezioniNote: i permessi esistenti adesso. Quelli che nasceranno dopo partiranno dal
      // loro default di ruolo invece che «no» (utils/permessiEffettivi.js, regole 65)
      await setDoc(doc(db, "abilitazioniSezioni", effectiveOrgId), {
        orgId: effectiveOrgId, ruoli: ruoliObj, utenti: utentiObj, utentiRimossi: utentiRimossiObj,
        sezioniNote: catalogoSezioni(), salvatoIl: serverTimestamp(),
      });
      addToast("Abilitazioni salvate ✅", "success");
    } catch (err) { addToast(messaggioErrore(err), "error"); }
    setSaving(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>Caricamento...</div>;

  return (
    <>
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "14px 24px", flexShrink: 0 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <Shield size={18} color="#7c3aed" /> Abilitazioni Sezioni
            <button onClick={salva} className="btn btn-primary" disabled={saving}
              style={{ fontSize: 13, padding: "4px 12px" }}>
              {saving ? "Salvo..." : "💾 Salva modifiche"}
            </button>
          </h2>
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
          Controlla quali sezioni sono visibili per ogni ruolo. Owner e Admin vedono sempre tutto.
        </div>
      </div>

      {/* Tab */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "0 24px", flexShrink: 0, display: "flex", gap: 2 }}>
        {[{ id: "ruoli", label: "Per ruolo" }, { id: "utenti", label: "Per utente" }, { id: "tab-sidebar", label: "🗂️ Visibilità Tab" }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: "8px 16px", border: "none", borderBottom: activeTab === t.id ? "2px solid #7c3aed" : "2px solid transparent", background: "none", color: activeTab === t.id ? "#7c3aed" : "#6b7280", fontWeight: activeTab === t.id ? 700 : 400, cursor: "pointer", fontFamily: "'Inter',sans-serif", fontSize: 13 }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
        {activeTab === "ruoli" && (
          <>
            {maiSalvata && (
              <div style={{ background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:8, padding:"10px 14px", marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:15, flexShrink:0 }}>💜</span>
                <span style={{ fontSize:12, color:"#5b21b6" }}>
                  Uno o più ruoli mostrano ancora i permessi di partenza, non personalizzati per questa azienda — sono gli stessi mostrati in Impostazioni Aziendali → Ruoli Operativi. Modificali e salva per renderli specifici per la tua organizzazione.
                </span>
              </div>
            )}
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14, flexWrap:"wrap" }}>
              <div style={{ position:"relative", flex:1, minWidth:220, maxWidth:360 }}>
                <Search size={13} style={{ position:"absolute", left:10, top:10, color:"#9ca3af" }}/>
                <input value={cercaSez} onChange={e=>setCercaSez(e.target.value)}
                  placeholder="Cerca una sezione — es. magazzino, ferie, offerte..."
                  style={{ width:"100%", padding:"8px 10px 8px 30px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:12.5, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" }}/>
              </div>
              <button onClick={()=>setGruppiAperti(tuttiAperti ? {} : Object.fromEntries(gruppi.map(g=>[g.id,true])))}
                style={{ padding:"7px 12px", border:"1px solid #e5e7eb", borderRadius:8, background:"#fff", color:"#6b7280", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                {tuttiAperti ? "Chiudi tutti" : "Apri tutti"}
              </button>
              <span style={{ fontSize: 11.5, color: "#9ca3af", marginLeft:"auto" }}>
                ✅ = visibile per quel ruolo · ☐ = nascosta
              </span>
            </div>

            {gruppi.map(gruppo => {
              const voci = gruppo.voci.filter(s => !cercaSez.trim() ||
                (s.label+" "+s.desc).toLowerCase().includes(cercaSez.trim().toLowerCase()));
              if (voci.length === 0) return null;
              // Durante una ricerca i gruppi si aprono da soli: cercare e poi dover
              // anche aprire il gruppo sarebbe un passaggio in più senza motivo.
              const aperto = cercaSez.trim() ? true : !!gruppiAperti[gruppo.id];
              const spuntate = voci.reduce((n,s)=>n + RUOLI.filter(r=>abilitazioni[r.id]?.has(s.id)).length, 0);
              return (
                <div key={gruppo.id} style={{ border:"1px solid #e5e7eb", borderRadius:10, marginBottom:10, overflow:"hidden" }}>
                  <button onClick={()=>setGruppiAperti(g=>({...g,[gruppo.id]:!g[gruppo.id]}))}
                    style={{ width:"100%", display:"flex", alignItems:"center", gap:9, padding:"11px 14px", background: aperto?"#faf5ff":"#fff",
                      border:"none", borderBottom: aperto?"1px solid #e9d5ff":"none", cursor:"pointer", fontFamily:"'Inter',sans-serif", textAlign:"left" }}>
                    <span style={{ fontSize:15 }}>{gruppo.emoji}</span>
                    <span style={{ fontSize:13, fontWeight:800, color:"#1e1b4b", flex:1 }}>{gruppo.label}</span>
                    <span style={{ fontSize:11, color:"#9ca3af" }}>{voci.length} sezion{voci.length===1?"e":"i"} · {spuntate} spunt{spuntate===1?"a":"e"}</span>
                    <span style={{ fontSize:12, color:"#9ca3af" }}>{aperto ? "▾" : "▸"}</span>
                  </button>
                  {aperto && (
                    <div style={{ overflowX:"auto" }}>
                      <table style={{ borderCollapse:"collapse", fontSize:12, width:"100%", minWidth:700 }}>
                        <thead>
                          <tr style={{ background:"#f9fafb", borderBottom:"1px solid #e5e7eb" }}>
                            <th style={{ padding:"8px 16px", textAlign:"left", fontWeight:700, color:"#6b7280", minWidth:240 }}>Sezione</th>
                            {RUOLI.map(r => (
                              <th key={r.id} style={{ padding:"8px 12px", textAlign:"center", fontWeight:700, color:"#6b7280", minWidth:104 }}>{r.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {voci.map((sez, si) => {
                            const figlio = !!SEZIONE_PADRE[sez.id];
                            return (
                              <tr key={sez.id} style={{ borderBottom:"1px solid #f3f4f6", background: si%2===0 ? "#fff" : "#fafafa" }}>
                                <td style={{ padding: figlio ? "8px 16px 8px 34px" : "10px 16px" }}>
                                  <div style={{ fontWeight: figlio?500:600, color: figlio?"#374151":"#111827", fontSize: figlio?12:12.5,
                                    display:"flex", alignItems:"center", gap:5 }}>
                                    {figlio && <span style={{ color:"#d1d5db" }}>↳</span>}
                                    {sez.label}
                                  </div>
                                  <div style={{ fontSize:11, color:"#9ca3af" }}>{sez.desc}</div>
                                </td>
                                {RUOLI.map(ruolo => {
                                  const abilitato = abilitazioni[ruolo.id]?.has(sez.id);
                                  return (
                                    <td key={ruolo.id} style={{ padding:"8px 12px", textAlign:"center" }}>
                                      <button type="button" aria-label={`${sez.label} — ${ruolo.label}`} aria-pressed={!!abilitato}
                                        onClick={() => toggleRuolo(ruolo.id, sez.id)}
                                        style={{
                                          width:26, height:26, borderRadius:6, border:`2px solid ${abilitato ? "#7c3aed" : "#e5e7eb"}`,
                                          background: abilitato ? "#7c3aed" : "#fff", cursor:"pointer",
                                          display:"inline-flex", alignItems:"center", justifyContent:"center"
                                        }}>
                                        {abilitato && <Check size={13} color="#fff" />}
                                      </button>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
            {cercaSez.trim() && gruppi.every(g => g.voci.filter(s=>(s.label+" "+s.desc).toLowerCase().includes(cercaSez.trim().toLowerCase())).length===0) && (
              <div style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:12.5 }}>
                Nessuna sezione trovata per «{cercaSez}».
              </div>
            )}
          </>
        )}

        {activeTab === "utenti" && (
          <>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:8 }}>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>
                Eccezioni individuali — si sommano al ruolo, non lo sostituiscono. Utile per dare o togliere qualcosa a una persona specifica.
              </div>
              <button onClick={()=>{ setSelezionatiGestione(new Set()); setShowPopupGestione(true); }}
                style={{ fontSize:12, padding:"6px 16px", border:"1px solid #7c3aed", borderRadius:20,
                  background:"#ede9fe", color:"#7c3aed", fontWeight:700, cursor:"pointer",
                  fontFamily:"'Inter',sans-serif" }}>
                ＋ Abilita sezione Gestione
              </button>
            </div>
            {utenti.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Nessun utente da configurare</div>
            ) : utenti.map(u => {
              const effettive = sezioniEffettive(u.uid, u.ruoloOperativo);
              const aggiunte = utentiCustom[u.uid] || new Set();
              const rimosse = utentiRimossi[u.uid] || new Set();
              return (
                <div key={u.uid} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, marginBottom: 14 }}>
                  {/* Header utente */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#7c3aed", flexShrink: 0 }}>
                      {(u.displayName||"?")[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{u.displayName}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        Ruolo: <strong>{RUOLI_OPERATIVI.find(r=>r.id===u.ruoloOperativo)?.label || "nessun ruolo assegnato"}</strong>
                        {aggiunte.size > 0 && <span style={{ marginLeft: 8, color: "#059669", fontWeight: 600 }}>+{aggiunte.size} aggiunte</span>}
                        {rimosse.size > 0 && <span style={{ marginLeft: 8, color: "#dc2626", fontWeight: 600 }}>−{rimosse.size} rimosse</span>}
                      </div>
                    </div>
                  </div>

                  {/* Legenda */}
                  <div style={{ display: "flex", gap: 16, marginBottom: 10, fontSize: 10, color: "#9ca3af" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: "#7c3aed", display: "inline-block" }}/>
                      Eredita dal ruolo
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: "#059669", display: "inline-block" }}/>
                      Aggiunta per questo utente
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: "#dc2626", display: "inline-block" }}/>
                      Rimossa per questo utente
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: "#f3f4f6", border: "1px solid #e5e7eb", display: "inline-block" }}/>
                      Non abilitata
                    </span>
                  </div>

                  {/* Sezioni raggruppate per argomento — con 36 voci una fila unica di
                      pillole tutte uguali è illeggibile: non si trova quello che si cerca
                      e non si capisce cosa si sta guardando. */}
                  <div style={{ marginBottom: 14 }}>
                  {gruppi.map(gruppo => (
                    <div key={gruppo.id} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 5, display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ fontSize: 12 }}>{gruppo.emoji}</span> {gruppo.label}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {gruppo.voci.map(sez => {
                      const aggiunta = aggiunte.has(sez.id);
                      const rimossa = rimosse.has(sez.id);
                      const ereditata = (abilitazioni[u.ruoloOperativo] || new Set()).has(sez.id);
                      // Stato visivo
                      let bg, border, col, simbolo, title;
                      if (rimossa) {
                        bg = "#fef2f2"; border = "#fca5a5"; col = "#dc2626"; simbolo = "✕"; title = "Rimossa per questo utente — clicca per ripristinare";
                      } else if (aggiunta) {
                        bg = "#f0fdf4"; border = "#86efac"; col = "#059669"; simbolo = "✚"; title = "Aggiunta per questo utente — clicca per rimuovere";
                      } else if (ereditata) {
                        bg = "#ede9fe"; border = "#c4b5fd"; col = "#7c3aed"; simbolo = "✓"; title = "Ereditata dal ruolo — clicca per revocare";
                      } else {
                        bg = "#fff"; border = "#e5e7eb"; col = "#9ca3af"; simbolo = "○"; title = "Non abilitata — clicca per aggiungere";
                      }
                      return (
                        <button key={sez.id}
                          onClick={() => toggleUtenteSezione(u.uid, sez.id, u.ruoloOperativo)}
                          title={title}
                          style={{
                            padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                            cursor: "pointer", fontFamily: "'Inter',sans-serif",
                            border: `1px solid ${border}`, background: bg, color: col,
                            display: "flex", alignItems: "center", gap: 4
                          }}>
                          <span style={{ fontSize: 10 }}>{simbolo}</span>
                          {SEZIONE_PADRE[sez.id] && <span style={{ opacity:.5, fontSize:10 }}>↳</span>}
                          {sez.label}
                        </button>
                      );
                    })}
                      </div>
                    </div>
                  ))}
                  </div>

                  {/* Riepilogo finale — apribile: ripetere tutte le pillole già mostrate
                      sopra raddoppiava la lunghezza della scheda senza aggiungere nulla.
                      Chiuso mostra solo il conteggio, che è l'informazione che conta. */}
                  <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "9px 14px" }}>
                    {effettive.size === 0 ? (
                      <div style={{ fontSize: 11.5, color: "#dc2626", fontWeight: 600 }}>⚠️ Nessuna sezione abilitata — {u.displayName.split(" ")[0]} vedrà solo le aree base</div>
                    ) : (
                      <>
                        <button onClick={()=>setRiepilogoAperto(r=>({...r,[u.uid]:!r[u.uid]}))}
                          style={{ display:"flex", alignItems:"center", gap:6, width:"100%", background:"none", border:"none", padding:0, cursor:"pointer", fontFamily:"'Inter',sans-serif", textAlign:"left" }}>
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: "#374151" }}>
                            ✅ {u.displayName.split(" ")[0]} vede {effettive.size} sezioni su {SEZIONI.length}
                          </span>
                          <span style={{ fontSize: 11, color: "#9ca3af", marginLeft:"auto" }}>
                            {riepilogoAperto[u.uid] ? "nascondi elenco ▾" : "vedi elenco ▸"}
                          </span>
                        </button>
                        {riepilogoAperto[u.uid] && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
                            {SEZIONI.filter(s => effettive.has(s.id)).map(s => (
                              <span key={s.id} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "#e0e7ff", color: "#3730a3", fontWeight: 600 }}>
                                {s.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
        {activeTab === "tab-sidebar" && (
          <>
            <div style={{ fontSize:12, color:"#9ca3af", marginBottom:20 }}>
              Controlla quali tab della sidebar sono visibili per ogni utente. Di default tutti vedono tutti i tab. Owner e Admin vedono sempre tutto indipendentemente da questa impostazione.
            </div>
            <div style={{ background:"#fef3c7", border:"1px solid #fde68a", borderRadius:10, padding:"12px 16px", marginBottom:20, fontSize:12, color:"#92400e" }}>
              ⚠️ <strong>Attenzione:</strong> nascondere un tab non rimuove le abilitazioni alle singole sezioni. Se un utente ha accesso a "Presenze" ma non vede il Tab Operai, non potrà raggiungere quella sezione. Assicurati che le impostazioni siano coerenti.
            </div>
            {utenti.length===0 ? (
              <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Nessun utente da configurare</div>
            ) : utenti.map(u => {
              // Leggi tab visibili per questo utente (default: tutti)
              const tabAbilitatiU = utentiCustom[u.uid] 
                ? new Set([...Array.from(utentiCustom[u.uid])].filter(s=>s.startsWith("tab-"))) 
                : new Set(TAB_SIDEBAR.map(t=>t.id));
              const hasTuttiTab = TAB_SIDEBAR.every(t=>tabAbilitatiU.has(t.id));
              return (
                <div key={u.uid} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:16, marginBottom:14 }}>
                  <div style={{ fontSize:14, fontWeight:700, marginBottom:10, color:"#111827", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <span>{u.displayName} <span style={{ fontSize:11, color:"#9ca3af", fontWeight:400 }}>{RUOLI_OPERATIVI.find(r=>r.id===u.ruoloOperativo)?.label || ""}</span></span>
                    {hasTuttiTab && <span style={{ fontSize:11, color:"#059669", background:"#d1fae5", padding:"2px 8px", borderRadius:8 }}>Vede tutti i tab</span>}
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                    {TAB_SIDEBAR.map(tab => {
                      const abilitato = tabAbilitatiU.has(tab.id);
                      return (
                        <button key={tab.id} onClick={()=>toggleUtenteSezione(u.uid, tab.id, u.ruoloOperativo)}
                          style={{ padding:"8px 14px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif",
                            border:`2px solid ${abilitato?"#7c3aed":"#e5e7eb"}`,
                            background:abilitato?"#ede9fe":"#fff", color:abilitato?"#7c3aed":"#9ca3af" }}>
                          {abilitato?"✓":"✕"} {tab.label}
                          <div style={{ fontSize:10, fontWeight:400, color:abilitato?"#7c3aed":"#d1d5db", marginTop:2 }}>{tab.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>

      {/* Popup Abilita sezione Gestione */}
      {showPopupGestione && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#fff", borderRadius:16, padding:24, width:440, maxHeight:"80vh", overflow:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize:16, fontWeight:800, color:"#1e1b4b", marginBottom:6 }}>Abilita sezione Gestione</div>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:16 }}>
              Seleziona gli utenti a cui abilitare tutte le voci della sezione Gestione.
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:20 }}>
              {utenti.length === 0 && (
                <div style={{ textAlign:"center", padding:20, color:"#9ca3af", fontSize:13 }}>Nessun utente disponibile</div>
              )}
              {utenti.map(u => {
                const sel = selezionatiGestione.has(u.uid);
                return (
                  <label key={u.uid} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px",
                    border:`1px solid ${sel?"#7c3aed":"#e5e7eb"}`, borderRadius:10,
                    background: sel?"#ede9fe":"#fff", cursor:"pointer" }}>
                    <input type="checkbox" checked={sel}
                      onChange={e => {
                        const nuovi = new Set(selezionatiGestione);
                        e.target.checked ? nuovi.add(u.uid) : nuovi.delete(u.uid);
                        setSelezionatiGestione(nuovi);
                      }}
                      style={{ accentColor:"#7c3aed", width:16, height:16 }}/>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{u.displayName}</div>
                      <div style={{ fontSize:11, color:"#9ca3af" }}>{RUOLI_OPERATIVI.find(r=>r.id===u.ruoloOperativo)?.label || "nessun ruolo assegnato"}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button onClick={()=>setShowPopupGestione(false)}
                style={{ padding:"8px 18px", border:"1px solid #e5e7eb", borderRadius:8, background:"#fff",
                  color:"#374151", fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                Annulla
              </button>
              <button onClick={()=>{
                selezionatiGestione.forEach(uid => {
                  const sezioniAttuali = utentiCustom[uid] || new Set();
                  const nuove = new Set(sezioniAttuali);
                  SEZIONI_GESTIONE.forEach(id => nuove.add(id));
                  setUtentiCustom(prev=>({...prev,[uid]:nuove}));
                });
                setShowPopupGestione(false);
              }}
                disabled={selezionatiGestione.size === 0}
                style={{ padding:"8px 18px", border:"none", borderRadius:8,
                  background: selezionatiGestione.size>0?"#7c3aed":"#e5e7eb",
                  color: selezionatiGestione.size>0?"#fff":"#9ca3af",
                  fontSize:13, fontWeight:700,
                  cursor: selezionatiGestione.size>0?"pointer":"not-allowed",
                  fontFamily:"'Inter',sans-serif" }}>
                Abilita ({selezionatiGestione.size})
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}